// A specialist's own numbers: their link, the companies they've registered,
// where each one stands, what they've earned and what they've been paid. The
// server chooses the rows from the session, and every figure here is one it
// sent: nothing is worked out in the browser.
import React, { useCallback, useEffect, useRef, useState } from "react";
import SEO from "../components/SEO";
import { copyToClipboard } from "../components/CopyLinkButton";
import {
  REGISTER_ERRORS,
  STATUS_LABELS,
  STATUS_TONES,
  day,
  loadDashboard,
  money,
  moneyTone,
  registerCompany,
  signOut,
} from "../components/specialistApi";
import "./specialists.css";

const COPIED_MS = 2000;
const COPIED = "Link copied";

function LinkRow({ label, url }) {
  const [note, setNote] = useState("");
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = async () => {
    const done = await copyToClipboard(url);
    setNote(done ? COPIED : "Couldn't copy it");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setNote(""), COPIED_MS);
  };
  return (
    <div className="specialistLinkRow">
      <span className="specialistLinkText" aria-label={label}>
        {url}
      </span>
      <button className="btn btnGhost specialistGood" type="button" onClick={copy}>
        Copy
      </button>
      <span className={`specialistCopied ${note === COPIED ? "specialistGood" : note ? "specialistBad" : ""}`} role="status">
        {note}
      </span>
    </div>
  );
}

function RegisterForm({ active, onRegistered }) {
  const [form, setForm] = useState({ company: "", contactName: "", contactEmail: "" });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setStatus("");
    const out = await registerCompany(form);
    setBusy(false);
    if (out.ok) {
      setStatus(`${out.data.introduction.company} is registered to you. Eric will answer within 5 business days.`);
      setForm({ company: "", contactName: "", contactEmail: "" });
      onRegistered();
      return;
    }
    const code = out.status === 429 ? "rate_limited" : out.data?.error;
    if (code === "already_registered") {
      setStatus(out.data.yours ? "You've already registered that company." : "Another specialist already holds that company.");
      return;
    }
    setStatus(REGISTER_ERRORS[code] || "That didn't save. Please try again.");
  };

  if (!active) {
    return <p className="muted specialistEmpty">Your agreement has ended, so you can't register new companies.</p>;
  }
  return (
    <>
      <p className="muted specialistFine">
        Register a company before they talk to BossDrop. The earliest registration holds it. Make sure the person knows
        BossDrop pays you.
      </p>
      <form className="formGrid" onSubmit={submit} style={{ marginTop: 12 }}>
        <div className="field">
          <label htmlFor="register-company">Company</label>
          <input id="register-company" required maxLength={160} value={form.company} onChange={set("company")} placeholder="Acme Inc." />
        </div>
        <div className="field">
          <label htmlFor="register-contact">The person you spoke to</label>
          <input id="register-contact" required maxLength={120} value={form.contactName} onChange={set("contactName")} placeholder="Pat Lee, VP Operations" />
        </div>
        <div className="field">
          <label htmlFor="register-email">Their email (optional)</label>
          <input id="register-email" type="email" maxLength={200} value={form.contactEmail} onChange={set("contactEmail")} placeholder="pat@acme.com" />
        </div>
        <div className="ctaRow field">
          <button className="btn btnCream" type="submit" disabled={busy}>
            {busy ? "Registering..." : "Register company"}
          </button>
        </div>
      </form>
      <p className="formStatus" role="status">
        {status}
      </p>
    </>
  );
}

function ledgerWhat(row) {
  if (row.kind === "payout") return Number(row.amountCents) < 0 ? "Payout correction" : "Payout to you";
  return Number(row.amountCents) < 0 ? `Refund: ${row.company}` : `Payment: ${row.company}`;
}

export default function SpecialistDashboard({ go = (path) => window.location.assign(path) }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    const out = await loadDashboard();
    if (out.status === 401) {
      go("/specialists/login");
      return;
    }
    if (!out.ok) {
      setFailed(true);
      return;
    }
    setFailed(false);
    setData(out.data);
    // go comes from the props once, at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const leave = async () => {
    await signOut();
    go("/specialists");
  };

  const seo = (
    <SEO
      title="BossDrop | Specialist dashboard"
      description="Your BossDrop Implementation Specialist dashboard."
      canonical="https://bossdrop.ai/specialists/dashboard"
      url="https://bossdrop.ai/specialists/dashboard"
      robots="noindex,nofollow"
    />
  );

  if (!data) {
    return (
      <section className="page container specialistPortal">
        {seo}
        <div className="hero">
          <h1 className="h1">Your dashboard</h1>
          <p className="sub" role="status">
            {failed ? "Couldn't load your dashboard. Please refresh to try again." : "Loading..."}
          </p>
        </div>
      </section>
    );
  }

  const { specialist, summary, introductions, ledger } = data;
  const site = window.location.origin;
  return (
    <section className="page container specialistPortal">
      {seo}
      <div className="hero specialistTop">
        <div>
          <h1 className="h1">Hi, {specialist.name.split(/\s+/)[0]}</h1>
          <p className="sub">
            Your share is {specialist.sharePct}% of what a customer you brought pays BossDrop for their first 12 months.
          </p>
          {/* So nobody registers companies on an account opened through someone else's link. */}
          <p className="muted specialistFine">Signed in as {specialist.email}</p>
        </div>
        <button className="btn btnGhost specialistBad" type="button" onClick={leave}>
          Sign out
        </button>
      </div>

      <div className="specialistTiles">
        <div className="specialistTile">
          <div className="specialistTileLabel">Earned so far</div>
          <div className={`specialistTileValue ${moneyTone(summary.earnedCents)}`}>{money(summary.earnedCents)}</div>
        </div>
        <div className="specialistTile">
          <div className="specialistTileLabel">Paid to you</div>
          <div className={`specialistTileValue ${moneyTone(summary.paidCents)}`}>{money(summary.paidCents)}</div>
        </div>
        <div className="specialistTile">
          <div className="specialistTileLabel">{summary.owedCents < 0 ? "You owe back" : "Owed to you"}</div>
          <div className={`specialistTileValue ${summary.owedCents > 0 ? "specialistCream" : moneyTone(summary.owedCents)}`}>
            {money(Math.abs(summary.owedCents))}
          </div>
        </div>
      </div>
      <p className="muted specialistFine">
        You earn when a customer you brought pays BossDrop. Payouts go out by the 15th of the month after each payment
        clears.
      </p>

      <div className="panel">
        <h2 className="h2" style={{ marginTop: 0 }}>
          Your link
        </h2>
        <p className="muted specialistFine">A demo booked through it registers the company to you. Say you're paid when you share it.</p>
        <LinkRow label="Link to the website" url={`${site}/?ref=${specialist.code}`} />
        <LinkRow label="Link to the calendar" url={`${site}/book-a-demo?ref=${specialist.code}`} />
      </div>

      <div className="panel">
        <h2 className="h2" style={{ marginTop: 0 }}>
          Register a company
        </h2>
        <RegisterForm active={specialist.status === "active"} onRegistered={load} />
      </div>

      <div className="panel">
        <h2 className="h2" style={{ marginTop: 0 }}>
          Your companies
        </h2>
        {introductions.length ? (
          <div className="specialistTableWrap">
            <table className="specialistTable">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Where it stands</th>
                  <th>Registered</th>
                  <th>Held until</th>
                  <th>Your share ends</th>
                </tr>
              </thead>
              <tbody>
                {introductions.map((row) => (
                  <tr key={row.id}>
                    <td>
                      {row.company}
                      {row.contactName ? <div className="specialistRowNote">{row.contactName}</div> : null}
                    </td>
                    <td>
                      <span className={STATUS_TONES[row.status] || ""}>{STATUS_LABELS[row.status] || row.status}</span>
                      {row.note ? <div className="specialistRowNote">{row.note}</div> : null}
                    </td>
                    <td>{day(row.registeredAt)}</td>
                    <td>{day(row.protectedUntil)}</td>
                    <td>{day(row.shareEndsOn)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted specialistEmpty">Nothing registered yet. Add your first company above.</p>
        )}
      </div>

      <div className="panel">
        <h2 className="h2" style={{ marginTop: 0 }}>
          Earnings and payouts
        </h2>
        {ledger.length ? (
          <div className="specialistTableWrap">
            <table className="specialistTable">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>What</th>
                  <th className="num">Customer paid</th>
                  <th className="num">Your share</th>
                  <th className="num">Paid to you</th>
                  <th>Payable by</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((row) => (
                  <tr key={row.id}>
                    <td>{day(row.occurredOn)}</td>
                    <td>
                      {ledgerWhat(row)}
                      {row.note ? <div className="specialistRowNote">{row.note}</div> : null}
                    </td>
                    <td className={`num ${moneyTone(row.receivedCents)}`}>{row.receivedCents == null ? "" : money(row.receivedCents)}</td>
                    <td className={`num ${row.kind === "earned" ? moneyTone(row.amountCents) : ""}`}>{row.kind === "earned" ? money(row.amountCents) : ""}</td>
                    <td className={`num ${row.kind === "payout" ? moneyTone(row.amountCents) : ""}`}>{row.kind === "payout" ? money(row.amountCents) : ""}</td>
                    <td>{day(row.payableBy)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted specialistEmpty">
            No earnings yet. Your first share comes when a customer you brought makes their first payment. With the pilot
            first, expect about three months or more from an introduction.
          </p>
        )}
      </div>
    </section>
  );
}
